import axiosNest from '../../api/axiosNest';
import { capitalizeFirst } from '../../shared/sentence';

/**
 * Wywołania operacji na zleceniu.
 *
 * Odpowiada endpointom z `apps/api/src/user-offers/operacje.controller.ts`.
 *
 * ## Dlaczego `axiosNest`, a nie dataProvider react-admina
 *
 * To nie są operacje CRUD nad zasobem — to czynności na rekordzie („obsadź",
 * „zgłoś ryzyko"), każda z własnym adresem i własnymi regułami. Wciskanie ich
 * w `update()` dataProvidera zrobiłoby z niego worek na wszystko.
 *
 * `axiosNest` to przeniesiony z FreightFusion klient z interceptorem tokenu.
 *
 * ⚠️ **Uwaga na stan zastany:** `admin/dataProvider.ts` (też przeniesiony
 * i przetestowany) **nie jest nigdzie podpięty** — `App.tsx` używa
 * `simpleRestProvider` z własnym `httpClient`. Oba czytają ten sam token
 * z `localStorage`, więc dziś nic z tego nie wynika, ale dwie drogi
 * uwierzytelniania w jednej aplikacji to dług do spłacenia osobną zmianą.
 *
 * ## Komunikaty błędów
 *
 * Backend odmawia operacji **z uzasadnieniem** („Zlecenie nie jest
 * potwierdzone — najpierw akceptacja, potem obsadzenie"). Te komunikaty są
 * pisane dla operatora i mają do niego dotrzeć w całości — dlatego
 * `toErrorMessage` wyciąga treść z odpowiedzi, zamiast pokazywać „błąd
 * serwera".
 */

export interface OperationContext {
  reason?: string;
  /**
   * Obsadzenie mimo blokady z uprawnień — Team Leader z powodem (odpowiedzi
   * Marathonu/Euvic 16.10.2026, pyt. 54e; `AssignOverride.tsx`).
   */
  overrideBlocking?: boolean;
  /**
   * Powód z listy przy przekazaniu do Trade i zwrocie do FTL (makieta back
   * office v2, A6; przełącznik `tradeTransferReasons`) — `reason` jest wtedy
   * komentarzem. Tylko przy zmianie stanu.
   */
  reasonCode?: string;
}

/** Odpowiedź błędu z NestJS-a. */
type ErrorResponse = {
  response?: { data?: { message?: unknown } };
  message?: string;
};

/**
 * Treść odmowy do pokazania operatorowi.
 *
 * NestJS potrafi zwrócić `message` jako tekst albo tablicę (walidacja), więc
 * obsługujemy oba. Gdy nic sensownego nie przyszło, mówimy wprost, że nie
 * wiadomo — zamiast zmyślać powód.
 *
 * Komunikat pokazujemy samodzielnie, więc od wielkiej litery (07.10.2026) —
 * odmowy z API bywają pisane małą, bo wkleja się je też w dłuższe zdania.
 */
export function toErrorMessage(err: unknown): string {
  const b = err as ErrorResponse;
  const message = b?.response?.data?.message;
  if (typeof message === 'string' && message.trim() !== '') return capitalizeFirst(message);
  if (Array.isArray(message)) {
    const joined = message.filter((x) => typeof x === 'string').join(', ');
    if (joined.trim() !== '') return capitalizeFirst(joined);
  }
  if (typeof b?.message === 'string' && b.message.trim() !== '') return b.message;
  return 'Operacja nie powiodła się — brak szczegółów od serwera';
}

/** Puste uzasadnienie nie ma trafiać do dziennika jako pusty ciąg. */
function reason(ctx?: OperationContext): string | undefined {
  const t = ctx?.reason?.trim();
  return t ? t : undefined;
}

const path = (id: number | string) => `/user-offers/${id}`;

/** Jawna zmiana stanu w cyklu życia banku. */
export async function changePlanningStatus(
  id: number | string,
  status: string,
  ctx?: OperationContext,
) {
  const code = ctx?.reasonCode?.trim();
  const { data } = await axiosNest.put(`${path(id)}/planning-status`, {
    status,
    reason: reason(ctx),
    // Bez kodu klucza nie ma — API sprzed powodów z listy go nie zna.
    ...(code ? { reasonCode: code } : {}),
  });
  return data;
}

/**
 * Skąd planista dodaje wariant (status 29.09.2026) — wartości jak
 * w `apps/api/src/user-offers/candidate-origin.ts`. Krótka lista pokazuje
 * po nim „dodany ręcznie”.
 */
export type CandidateOrigin = 'suggestion' | 'order_suggestion' | 'manual';

/** Wstępne przypisanie pojazdu — kandydatura. */
export async function addCandidate(
  id: number | string,
  carId: number,
  ctx?: OperationContext & { note?: string; origin?: CandidateOrigin },
) {
  const { data } = await axiosNest.post(`${path(id)}/candidates`, {
    carId,
    note: ctx?.note?.trim() || undefined,
    reason: reason(ctx),
    origin: ctx?.origin,
  });
  return data;
}

/** Odrzucenie podpowiedzi pojazdu z powodem (obowiązkowym) — wpis w dzienniku. */
export async function rejectSuggestion(id: number | string, carId: number, reasonText: string) {
  const { data } = await axiosNest.post(`${path(id)}/suggestions/${carId}/reject`, {
    reason: reasonText.trim(),
  });
  return data;
}

/** Zwolnienie kandydatury. Wiersz zostaje, zmienia się status. */
export async function releaseCandidate(
  id: number | string,
  carId: number,
  ctx?: OperationContext,
) {
  const { data } = await axiosNest.delete(`${path(id)}/candidates/${carId}`, {
    data: { reason: reason(ctx) },
  });
  return data;
}

/** Finalne obsadzenie — zwalnia pozostałe kandydatury. */
export async function assignCar(
  id: number | string,
  carId: number,
  ctx?: OperationContext,
) {
  const { data } = await axiosNest.put(`${path(id)}/assign-car`, {
    carId,
    reason: reason(ctx),
    overrideBlocking: ctx?.overrideBlocking === true ? true : undefined,
  });
  return data;
}

/**
 * Wynik „Obsadź” z listy rekomendacji — kształt jak
 * `SuggestionAssignmentResult` w `apps/api/src/user-offers/order-actions.service.ts`.
 */
export interface SuggestionAssignment {
  /** `false` — zlecenie już było obsadzone tym pojazdem; nic się nie zmieniło. */
  changed: boolean;
  carId: number;
  registrationNumber: string | null;
  /** Pojazd, który obsadzał zlecenie przed operacją; `null` = żaden. */
  previousCarId: number | null;
  planningStatus: string;
  variantAdded: boolean;
  releasedVariants: number;
  releasedElsewhere: string[];
  /** Zdania dla planisty (pojazd innej spółki, zwolnione warianty w innych zleceniach). */
  warnings: string[];
}

/**
 * „Obsadź” z listy rekomendowanych pojazdów (omówienie 01.10.2026) — jednym
 * kliknięciem: wariant, gdy pojazdu nie ma wśród aktywnych, i obsadzenie tymi
 * samymi regułami co „Zmień pojazd”. Do TMS nic nie idzie.
 */
export async function assignFromSuggestion(
  id: number | string,
  carId: number,
  ctx?: OperationContext,
): Promise<SuggestionAssignment> {
  const { data } = await axiosNest.post(`${path(id)}/assign-from-suggestion`, {
    carId,
    reason: reason(ctx),
    overrideBlocking: ctx?.overrideBlocking === true ? true : undefined,
  });
  return data as SuggestionAssignment;
}

/**
 * Zgłoszenie albo zdjęcie ryzyka.
 *
 * Przy zgłoszeniu backend wymaga powodu — alert bez wyjaśnienia jest dla
 * planisty bezużyteczny.
 */
export async function setRisk(
  id: number | string,
  atRisk: boolean,
  ctx?: OperationContext,
) {
  const { data } = await axiosNest.put(`${path(id)}/risk`, {
    atRisk,
    reason: reason(ctx),
  });
  return data;
}

/**
 * Termin ważności wariantu (makieta back office v2, 20.10.2026; przełącznik
 * `variantValidity`) — autor wariantu albo Team Leader, z powodem.
 * `validUntil` — chwila z przesunięciem (z pola `datetime-local` przez
 * `fromDateTimeLocalValue`).
 */
export async function setVariantValidUntil(
  id: number | string,
  carId: number,
  validUntil: string,
  ctx?: OperationContext,
) {
  const { data } = await axiosNest.put(`${path(id)}/candidates/${carId}/valid-until`, {
    validUntil,
    reason: reason(ctx),
  });
  return data;
}

/** Termin ważności. `null` kasuje termin. */
export async function setValidUntil(
  id: number | string,
  validUntil: string | null,
  ctx?: OperationContext,
) {
  const { data } = await axiosNest.put(`${path(id)}/valid-until`, {
    validUntil,
    reason: reason(ctx),
  });
  return data;
}

/**
 * Zmiana w TMS po obsadzeniu (warsztat Transporeon 29.09.2026) — alert
 * z importu, który czeka na „Potwierdzam, widziałem”. Kształt jak
 * `PendingTmsAlert` w `apps/api/src/user-offers/tms-change-alerts.ts`.
 */
export interface TmsAlert {
  id: number;
  /**
   * `replan` — alert o przeplanowaniu (osobny baner, `ReplanAlerts`);
   * `vehicleChange` — pojazd zmienił się w TMS po obsadzeniu (12.10.2026).
   */
  kind: 'change' | 'cancellation' | 'replan' | 'vehicleChange';
  /**
   * Ważność z reguły API (`apps/api/src/user-offers/alert-importance.ts`,
   * decyzja użytkownika 12.10.2026). Brak — odczyt z API sprzed zmiany.
   */
  importance?: 'important' | 'normal';
  /**
   * Tylko przy `cancellation`: TMS podał zlecenie na liście anulowanych
   * (przełącznik `tmsCancellations`, 19.10.2026) — wpis „anulowane w TMS”
   * albo podejrzenie, które TMS potwierdził. Decyzja nadal u planisty.
   */
  confirmedByTms?: true;
  /** Alert z kolejki Transporeonu (04.11.2026, `transporeonImport`). */
  source?: 'transporeon';
  text: string;
  reason: string | null;
  at: string;
}

/**
 * Czy alert jest ważny (czerwony, na górze). Bez pola z API: zmiany w TMS
 * są zawsze ważne, przeplanowanie — zwykłe (reguła API nie zgaduje pewności
 * bez sygnatury, więc i tu nie).
 */
export function isImportantAlert(alert: Pick<TmsAlert, 'kind' | 'importance'>): boolean {
  if (alert.importance) return alert.importance === 'important';
  return alert.kind !== 'replan';
}

/** Alerty zlecenia bez potwierdzenia, najnowsze pierwsze. */
export async function fetchTmsAlerts(id: number | string): Promise<TmsAlert[]> {
  const { data } = await axiosNest.get(`${path(id)}/tms-alerts`);
  const alerts = (data as { alerts?: unknown })?.alerts;
  return Array.isArray(alerts) ? (alerts as TmsAlert[]) : [];
}

/** „Potwierdzam, widziałem” — tylko wpis w dzienniku, bez innych skutków. */
export async function acknowledgeTmsAlerts(id: number | string, alertIds: number[]) {
  const { data } = await axiosNest.post(`${path(id)}/tms-alerts/acknowledge`, { alertIds });
  return data;
}

/** Najkrótszy powód decyzji — jak `MIN_DECISION_REASON_LENGTH` w API. */
export const MIN_DECISION_REASON_LENGTH = 10;

/**
 * „Potwierdzam anulację — zwolnij auto” — zdejmuje nasze obsadzenie,
 * zwalnia warianty i zamyka zlecenie jako nieaktualne. Do TMS nic nie idzie.
 */
export async function confirmTmsCancellation(
  id: number | string,
  alertIds: number[],
  reasonText: string,
) {
  const { data } = await axiosNest.post(`${path(id)}/tms-alerts/confirm-cancellation`, {
    alertIds,
    reason: reasonText.trim(),
  });
  return data;
}

/** „To nie anulacja” — potwierdzenie z powodem, plan zostaje bez zmian. */
export async function dismissTmsCancellation(
  id: number | string,
  alertIds: number[],
  reasonText: string,
) {
  const { data } = await axiosNest.post(`${path(id)}/tms-alerts/not-cancellation`, {
    alertIds,
    reason: reasonText.trim(),
  });
  return data;
}

/**
 * Wniosek o odpięcie pojazdu, który czeka na przełożonego (omówienie
 * 01.10.2026) — kształt jak `UnassignRequestView` w
 * `apps/api/src/user-offers/unassign-requests.ts`. Pole `unassignRequest`
 * szczegółów zlecenia; `null`, gdy nic nie czeka.
 */
export interface UnassignRequest {
  id: number;
  /**
   * Rodzaj wniosku (makieta back office v2, B6, 21.10.2026): `unassign`
   * (odpięcie), `tradeTransfer` (przekazanie do Trade po wyjeździe auta),
   * `vehicleChange` (zmiana pojazdu po wyjeździe). Brak — odpięcie.
   */
  kind?: string;
  carId: number | null;
  registrationNumber: string | null;
  /** Kod stanu docelowego: `ready`, `tentative`, `handed_to_trade`, przy zmianie pojazdu `assigned`. */
  toStatus: string;
  reason: string;
  /** Kod powodu z listy przy przekazaniu do Trade; `null` — powód tekstem. */
  reasonCode?: string | null;
  /** Proponowany pojazd przy zmianie pojazdu. */
  proposedCarId?: number | null;
  proposedRegistrationNumber?: string | null;
  requestedBy: { id: number; username: string } | null;
  createdAt: string;
  /** Czy zalogowany może zaakceptować albo odrzucić. */
  canDecide: boolean;
  /** Czy to wniosek zalogowanego — tylko on może go wycofać. */
  mine: boolean;
}

/** Najkrótszy powód wniosku i odrzucenia — jak `MIN_UNASSIGN_REASON_LENGTH` w API. */
export const MIN_UNASSIGN_REASON_LENGTH = 10;

/**
 * Wniosek o odpięcie pojazdu — dla planisty, który sam odpiąć nie może.
 * Auto zostaje przy zleceniu, dopóki przełożony nie zaakceptuje.
 */
export async function requestUnassign(
  id: number | string,
  toStatus: string,
  reasonText: string,
) {
  const { data } = await axiosNest.post(`${path(id)}/unassign-requests`, {
    toStatus,
    reason: reasonText.trim(),
  });
  return data;
}

/**
 * Akceptacja przełożonego — odpina pojazd (przy przekazaniu do Trade
 * przekazuje, przy zmianie pojazdu — obsadza proponowanym autem). Powód
 * opcjonalny; `overrideBlocking` — obsadzenie mimo blokady z uprawnień przy
 * zmianie pojazdu (Team Leader, powód wymagany jak przy „Obsadź”).
 */
export async function approveUnassign(
  id: number | string,
  requestId: number,
  reasonText?: string,
  options?: { overrideBlocking?: boolean },
) {
  const { data } = await axiosNest.post(
    `${path(id)}/unassign-requests/${requestId}/approve`,
    {
      reason: reasonText?.trim() || undefined,
      overrideBlocking: options?.overrideBlocking === true ? true : undefined,
    },
  );
  return data;
}

/**
 * Wniosek o przekazanie obsadzonego zlecenia do Trade po wyjeździe auta na
 * załadunek (makieta back office v2, B6, 21.10.2026; przełącznik
 * `tradeTransferBeforeDeparture`). `reasonCode` — powód z listy (A6),
 * `reason` jest wtedy komentarzem.
 */
export async function requestTradeTransfer(
  id: number | string,
  input: { reason: string; reasonCode?: string },
) {
  const { data } = await axiosNest.post(`${path(id)}/trade-transfer-requests`, {
    reason: input.reason.trim() || undefined,
    reasonCode: input.reasonCode || undefined,
  });
  return data;
}

/**
 * Wniosek o zmianę pojazdu po wyjeździe auta na załadunek (makieta back
 * office v2, B6, 21.10.2026; przełącznik `vehicleChangeRequest`) —
 * proponowane auto i powód; obsadza Team Leader akceptacją.
 */
export async function requestVehicleChange(
  id: number | string,
  carId: number,
  reasonText: string,
) {
  const { data } = await axiosNest.post(`${path(id)}/vehicle-change-requests`, {
    carId,
    reason: reasonText.trim(),
  });
  return data;
}

/** Odrzucenie przełożonego — z powodem; pojazd zostaje. */
export async function rejectUnassign(
  id: number | string,
  requestId: number,
  reasonText: string,
) {
  const { data } = await axiosNest.post(
    `${path(id)}/unassign-requests/${requestId}/reject`,
    { reason: reasonText.trim() },
  );
  return data;
}

/** Wycofanie wniosku przez wnioskującego. */
export async function withdrawUnassign(id: number | string, requestId: number) {
  const { data } = await axiosNest.post(
    `${path(id)}/unassign-requests/${requestId}/withdraw`,
  );
  return data;
}
